import { InitializationSteps } from './types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IdentityInstance } from '@/utils/identity-utils/identity-contract-module';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { DynamicAlertBanner } from '../alert-banners/DynamicAlertBanner';
import { cn, LucideIcon } from '@fileverse/ui';

interface NewUserFlowProps {
  startFrom: InitializationSteps;
  onCompleteSetup: (flow?: 'legacy' | 'new') => void;
}

export const NewUserFlow = ({
  startFrom,
  onCompleteSetup,
}: NewUserFlowProps) => {
  const [, setCurrentStep] = useState(startFrom);
  const [isError, setIsError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const isRunningRef = useRef(false);
  const [errorToastOpen, setErrorToastOpen] = useState(true);

  const setupNewUserIdentity = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    try {
      setIsError(false); // Reset error state before attempting

      switch (startFrom) {
        case InitializationSteps.SETUP_NEW_IDENTITY_MODULE:
          await IdentityInstance.setupNewIdentityModule();
          setCurrentStep(InitializationSteps.DEPLOY_PORTAL);
          await KeyStoreInstance.intialiseNewKeyStore((step) =>
            setCurrentStep(step)
          );
          break;

        case InitializationSteps.DEPLOY_PORTAL:
          await KeyStoreInstance.intialiseNewKeyStore((step) =>
            setCurrentStep(step)
          );
          break;
      }

      onCompleteSetup('new');
    } catch (error) {
      setIsError(true);
      console.error(error);
    } finally {
      isRunningRef.current = false; // Reset running ref regardless of outcome
    }
  }, [startFrom, onCompleteSetup]);

  useEffect(() => {
    setupNewUserIdentity();
  }, [setupNewUserIdentity]);

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        setErrorToastOpen(true);
      });
    }
  }, [isError, setErrorToastOpen]);

  return (
    <DynamicAlertBanner
      icon={
        <div className="flex items-center justify-center px-[9px] py-[5px] rounded color-bg-secondary">
          <span className="text-heading-lg">{isError ? '😕' : '👩‍🏭'}</span>
        </div>
      }
      title={
        !isError ? (
          <p>
            Finalising account creation
            <span className="animate-[dots_1s_ease-in-out_infinite]">...</span>
          </p>
        ) : (
          <p>Failed to create account</p>
        )
      }
      description={
        <div className="flex flex-col">
          <p>
            {
              'Once finalised, you’ll have full access to dDocs! This will only take a few seconds <3'
            }
          </p>
        </div>
      }
      footer={
        isError && (
          <>
            <div className="border-t color-border-default pt-2 px-3">
              <div className="flex justify-between items-center text-helper-text-sm color-text-secondary">
                <span className="flex flex-col gap-1">
                  <p className="color-text-danger">
                    {retryCount >= 3
                      ? 'No worries. No data loss happened. Please try again or contact us.'
                      : 'Oops, something went wrong'}
                  </p>
                </span>
                {retryCount < 3 && (
                  <span>
                    <button
                      className="flex justify-center items-center gap-1 color-text-default"
                      onClick={() => {
                        setRetryCount((prev) => prev + 1);
                        window.location.reload();
                      }}
                    >
                      <LucideIcon name="RotateCw" size="sm" />
                      {'Retry'}
                    </button>
                  </span>
                )}
              </div>
            </div>
            {retryCount >= 3 && (
              <div className="border-t color-border-default">
                <div className="flex justify-center items-center color-text-default text-helper-text-sm-bold">
                  <button
                    className="w-full h-[46px] flex justify-center items-center gap-1 hover:color-bg-secondary-hover transition-all border-r color-border-default"
                    onClick={() => {
                      setRetryCount((prev) => prev + 1);
                      window.location.reload();
                    }}
                  >
                    <LucideIcon name="RotateCw" size="sm" />
                    {'Retry'}
                  </button>
                  <button
                    className="w-full h-[46px] flex justify-center items-center gap-1 hover:color-bg-secondary-hover transition-all"
                    onClick={() => {
                      window.open('mailto:feedback@fileverse.io', '_blank');
                    }}
                  >
                    <LucideIcon name="Mail" size="sm" />
                    Contact us
                  </button>
                </div>
              </div>
            )}
          </>
        )
      }
      variant="default"
      className={cn(
        'border color-border-default flex flex-col gap-2 !px-0 transition-all',
        errorToastOpen ? 'invisible' : 'visible',
        retryCount >= 3 && isError && '!pb-0'
      )}
      contentClassName="px-3"
      isLoading={!isError}
    />
  );
};
